/**
 * usePermissions Hook
 * Provides permission checking utilities from AuthContext
 */
import { useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

export function usePermissions() {
  const { user } = useAuth();

  /**
   * Check if user is SuperAdmin
   */
  const isSuperAdmin = useCallback((): boolean => {
    if (!user?.roles) return false;
    return user.roles.includes('superadmin');
  }, [user]);

  /**
   * Check if user has a specific permission
   * SuperAdmin always has all permissions
   * Also checks department-level timesheet access for timesheet permissions
   */
  const hasPermission = useCallback((permissionKey: string): boolean => {
    // SuperAdmin has all permissions
    if (user?.roles?.includes('superadmin')) return true;
    
    // Check global permission first
    if (user?.permissions?.[permissionKey] === true) return true;
    
    // For timesheet-related permissions, also check department-level access
    const deptAccess = user?.department_timesheet_access;
    if (deptAccess) {
      // Map permission keys to department access flags
      if (permissionKey === 'timesheets.view' && deptAccess.has_any_view) return true;
      if (permissionKey === 'timesheets.approve' && deptAccess.has_any_approve) return true;
      if (permissionKey === 'timesheets.reject' && deptAccess.has_any_reject) return true;
      if (permissionKey === 'timesheets.update' && deptAccess.has_any_update) return true;
    }
    
    return false;
  }, [user]);

  /**
   * Check if user has ANY of the given permissions
   * SuperAdmin always returns true
   * Also considers department-level timesheet access
   */
  const hasAnyPermission = useCallback((permissionKeys: string[]): boolean => {
    // SuperAdmin has all permissions
    if (user?.roles?.includes('superadmin')) return true;
    
    // Check each permission using hasPermission (which includes dept access checks)
    return permissionKeys.some(key => hasPermission(key));
  }, [user, hasPermission]);

  /**
   * Check if user has ALL of the given permissions
   * SuperAdmin always returns true
   */
  const hasAllPermissions = useCallback((permissionKeys: string[]): boolean => {
    // SuperAdmin has all permissions
    if (user?.roles?.includes('superadmin')) return true;
    
    // Check each permission using hasPermission (which includes dept access checks)
    return permissionKeys.every(key => hasPermission(key));
  }, [user, hasPermission]);

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback((roleName: string): boolean => {
    if (!user?.roles) return false;
    return user.roles.includes(roleName);
  }, [user]);

  /**
   * Get all permissions user has
   */
  const getGrantedPermissions = useCallback((): string[] => {
    if (!user?.permissions) return [];
    return Object.entries(user.permissions)
      .filter(([_, granted]) => granted === true)
      .map(([key]) => key);
  }, [user]);

  /**
   * Check if user has department-level access for a specific permission type
   */
  const hasDepartmentAccess = useCallback((accessType: 'view' | 'approve' | 'reject' | 'update'): boolean => {
    const deptAccess = user?.department_timesheet_access;
    if (!deptAccess) return false;
    
    switch (accessType) {
      case 'view': return deptAccess.has_any_view;
      case 'approve': return deptAccess.has_any_approve;
      case 'reject': return deptAccess.has_any_reject;
      case 'update': return deptAccess.has_any_update;
      default: return false;
    }
  }, [user]);

  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isSuperAdmin,
    hasRole,
    getGrantedPermissions,
    hasDepartmentAccess,
    permissions: user?.permissions || {},
    roles: user?.roles || [],
    departmentTimesheetAccess: user?.department_timesheet_access || null,
  };
}

export default usePermissions;
